import { useRowViewQuery } from '@modules/board/hooks/useRowViewQuery';
import { Link, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { NodeViewWrapper } from '@tiptap/react';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'src/components/snackbar';
import { BoardAvatar } from 'src/features/board/BoardAvatar';
import { useLocales } from 'src/locales';

type MentionTableRowViewProps = {
  tableId: string;
  boardId: string;
  rowId: string;
  onClick?: () => void;
};

export const MentionTableRowNodeView = ({
  tableId,
  boardId,
  rowId,
  onClick,
}: MentionTableRowViewProps) => {
  const { translate } = useLocales();
  const { row, table, isLoading } = useRowViewQuery({
    boardId,
    tableId,
    rowId,
  });
  const { enqueueSnackbar } = useSnackbar();

  const title = useMemo(() => {
    if (isLoading) return translate('share.loading');

    if (!row || !table) return `${translate('error.http.not_found')}*`;

    const titleCol = table.columns.find((c) => c.field === 'title');
    if (!titleCol) return translate('shared.labels.untitled');

    const colValue = row.columns.find(
      (c) => c.columnId === titleCol._id,
    )?.value;

    return colValue || translate('shared.labels.untitled');
  }, [isLoading, row, table, translate]);

  const onClickAction = () => {
    if (isLoading) return undefined;
    if (!isLoading && !row) {
      enqueueSnackbar(translate('error.table_row.not_found'), {
        variant: 'error',
      });
      return undefined;
    }
    if (onClick) return onClick();
  };

  return (
    <NodeViewWrapper key={rowId} as="span">
      <Link
        component={RouterLink}
        to={''}
        onClick={onClickAction}
        sx={{
          display: 'inline-flex',
          verticalAlign: 'middle',
          color: 'var(--theme-color-primary)',
          textDecoration: 'none !important',
          width: 'fit-content',
          '&:hover': {
            textDecoration: 'none',
            backgroundColor: 'var(--theme-color-primary)',
            color: 'var(--theme-color-primary)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.5}>
          <BoardAvatar />
          <Typography component="span" variant="body2" sx={{ lineHeight: 1 }}>
            {title}
          </Typography>
        </Stack>
      </Link>
    </NodeViewWrapper>
  );
};
